import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
  type MRT_RowSelectionState
} from "mantine-react-table-open";
import { useMemo } from "react";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import {
  FREQUENCY_OPTIONS,
  MEDIUM_LABEL_MAP
} from "../../../../utils/constants";
import { DecimalLabel } from "../../../BuildingBlocks/DecimalLabel/DecimalLabel";
import { ORIGIN_LABEL_MAP, TYPE_LABEL_MAP } from "../../common";
import type { AcquisitionSummaryDataPoint } from "../EnergyDataPage/EnergyDataPage";
import type { Acquisition } from "../EnergyDataView";
import "./EnergyDataAcquisitionList.scss";

export type AcquisitionWithSummaryData = Exclude<
  Acquisition & Partial<AcquisitionSummaryDataPoint>,
  "energyDataAcquisition"
>;

interface EnergyDataAcquisitionListProps {
  acquisitions: Array<Acquisition>;
  acquisitionSummaryData?: Array<AcquisitionSummaryDataPoint>;
  showAll?: boolean;
  selection?: Array<AcquisitionWithSummaryData["id"]>;
  onSelect?: (selection: Array<AcquisitionWithSummaryData["id"]>) => void;
}

function EnergyDataAcquisitionList({
  acquisitions,
  showAll,
  acquisitionSummaryData,
  selection,
  onSelect
}: EnergyDataAcquisitionListProps) {
  const selectable = selection !== undefined;
  const showSummaryColumns = !!acquisitionSummaryData;
  const acqisitionsWithSummaryData: Array<AcquisitionWithSummaryData> = showAll
    ? acquisitions
    : acquisitions.reduce<Array<AcquisitionWithSummaryData>>(
        (acquisitions, acquisition) => {
          const summaryData = acquisitionSummaryData?.find(
            (data) => data.energyDataAcquisition.id === acquisition.id
          );

          if (showSummaryColumns && summaryData) {
            acquisitions.push({
              ...acquisition,
              energy: summaryData.energy,
              maxPower: summaryData.maxPower
            });
          }

          return acquisitions;
        },
        []
      );

  const totalEnergy = acqisitionsWithSummaryData.reduce(
    (total, acquisition) => {
      if (typeof acquisition.energy !== "undefined") {
        return total + acquisition.energy;
      }

      return total;
    },
    0
  );

  const columns: Array<MRT_ColumnDef<AcquisitionWithSummaryData>> =
    useMemo(() => {
      const baseColumns: Array<MRT_ColumnDef<AcquisitionWithSummaryData>> = [
        { header: "Liegenschaft", accessorKey: "site", size: 200 },
        { header: "Bezeichnung", accessorKey: "label", size: 450 },
        {
          header: "Typ",
          accessorKey: "type",
          accessorFn: (row) => TYPE_LABEL_MAP[row.type],
          size: 150
        },
        {
          header: "Medium",
          accessorKey: "medium",
          accessorFn: (row) => MEDIUM_LABEL_MAP[row.medium],
          size: 120
        },
        {
          accessorKey: "origin",
          header: "Herkunft",
          accessorFn: (row) => ORIGIN_LABEL_MAP[row.origin],
          size: 150
        },
        {
          accessorKey: "frequency",
          header: "Zeitintervall",
          accessorFn: (row) => FREQUENCY_OPTIONS[row.frequency]
        }
      ];
      const summaryColumns: Array<MRT_ColumnDef<AcquisitionWithSummaryData>> = [
        {
          header: "Energiemenge [kWh]",
          accessorKey: "energy",
          size: 170,
          Cell: ({ row }) =>
            row.original.maxPower === null ? (
              ""
            ) : (
              <span className="align-right">
                <DecimalLabel decimalScale={2} value={row.original.energy} />
              </span>
            ),
          Footer: () => (
            <span className="align-right">
              Summe: <DecimalLabel decimalScale={2} value={totalEnergy} />
            </span>
          )
        },
        {
          header: "Höchstleistung [kW]",
          accessorKey: "maxPower",
          size: 170,
          Cell: ({ row }) =>
            row.original.maxPower === null ? (
              ""
            ) : (
              <span className="align-right">
                <DecimalLabel decimalScale={2} value={row.original.maxPower} />
              </span>
            )
        }
      ];
      return showSummaryColumns
        ? [...baseColumns, ...summaryColumns]
        : baseColumns;
    }, [showSummaryColumns, totalEnergy]);

  const mrtTable = useMantineReactTable<AcquisitionWithSummaryData>({
    ...getDefaultMRTOptions({
      className: "EnergyDataAcquisitionList",
      initialState: {
        sorting: [
          { id: "site", desc: false },
          { id: "label", desc: false }
        ]
      },
      hideBorder: true
    }),
    enableToolbarInternalActions: false,
    state: {
      rowSelection:
        selection?.reduce((result, acquisitionId) => {
          result[acquisitionId] = true;
          return result;
        }, {}) || {}
    },
    onRowSelectionChange: (newSelection) => {
      if (typeof newSelection === "function") {
        const oldSelectionState: MRT_RowSelectionState =
          selection?.reduce((result, acquisitionId) => {
            result[acquisitionId] = true;
            return result;
          }, {}) || {};
        const newSelectionState = newSelection(oldSelectionState);
        const newSelectionArray =
          Object.keys(newSelectionState).reduce(
            (result, key) => {
              if (newSelectionState[key]) {
                result.push(parseInt(key, 10));
              }
              return result;
            },
            [] as Array<AcquisitionWithSummaryData["id"]>
          ) || [];
        onSelect?.(newSelectionArray);
      }
    },
    enableColumnResizing: true,
    enablePagination: false,
    enableSelectAll: selectable,
    enableRowSelection: selectable,
    columns,
    data: acqisitionsWithSummaryData
  });

  return <MantineReactTable table={mrtTable} />;
}

export { EnergyDataAcquisitionList, EnergyDataAcquisitionListProps };
